import { useAuth0 } from "@auth0/auth0-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import SpinningLoader from "../components/SpinningLoader";
import { create, getQuery, setAuthorization } from "../services/apiService";
import {
  getAuthToken,
  setAuthToken,
  setAuthData,
  clearAuthData,
  isTokenValid,
} from "./AuthActions";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [userProfile, setUserProfile] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const navigate = useNavigate();

  const {
    user: auth0User,
    isAuthenticated: auth0IsAuthenticated,
    isLoading: auth0IsLoading,
    getAccessTokenSilently,
    logout: auth0Logout,
  } = useAuth0();

  const initialLoadRef = useRef(false);
  const getUserPromiseRef = useRef(null);
  const fetchUserSettingsPromiseRef = useRef(null);
  const socialAuthProcessedRef = useRef(false);
  const logoutTimeoutRef = useRef(null);

  const isAuthenticated = useMemo(() => {
    if (isLoggingOut) return false;
    if (userProfile) {
      const token = getAuthToken();
      return isTokenValid(token);
    } else return false;
  }, [userProfile, isLoggingOut]);

  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);

    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
    }

    try {
      clearAuthData();
      sessionStorage.clear();

      setUserProfile(null);
      setSettings(null);

      setAuthorization(null);

      socialAuthProcessedRef.current = false;
      getUserPromiseRef.current = null;
      fetchUserSettingsPromiseRef.current = null;

      await new Promise((resolve) => setTimeout(resolve, 100));

      auth0Logout({
        logoutParams: {
          returnTo: `${window.location.origin}/landing`,
        },
      });

      logoutTimeoutRef.current = setTimeout(() => {
        navigate("/landing", { replace: true });
        setIsLoggingOut(false);
      }, 500);
    } catch (error) {
      console.error("Logout error:", error);
      setIsLoggingOut(false);

      navigate("/landing", { replace: true });
    }
  }, [auth0Logout, navigate]);

  useEffect(() => {
    return () => {
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
    };
  }, []);

  const fetchUserFromAPI = useCallback(
    async (tokenParam = null) => {
      if (isLoggingOut) return null;

      try {
        const token = tokenParam || getAuthToken();
        if (!token || !isTokenValid(token)) {
          return null;
        }

        setAuthorization(token);

        const response = await getQuery("/api/v1/user/get-account");
        if (response.status === 200 && response.data?.data) {
          const userData = response.data.data;

          if (userData.isBlocked === true) {
            if (!isLoggingOut) {
              clearAuthData();
              sessionStorage.clear();
              setAuthorization(null);
              setUserProfile(null);
              setSettings(null);
              navigate("/account/blocked", { replace: true });
            }
            return null;
          }

          return userData;
        }
        return null;
      } catch (error) {
        console.error("Error fetching user from API:", error);

        const pendingId = localStorage.getItem("pendingId");
        if (!isLoggingOut && !pendingId) {
          handleLogout();
        }
        return null;
      }
    },
    [handleLogout, isLoggingOut, navigate]
  );

  const getUser = useCallback(
    async (tokenParam = null) => {
      if (getUserPromiseRef.current) return getUserPromiseRef.current;

      const promise = fetchUserFromAPI(tokenParam)
        .then((data) => {
          if (data) {
            setUserProfile(data);
          }
          return data;
        })
        .finally(() => {
          getUserPromiseRef.current = null;
        });

      getUserPromiseRef.current = promise;
      return promise;
    },
    [fetchUserFromAPI]
  );

  const refreshUser = useCallback(
    async (tokenParam = null) => {
      if (getUserPromiseRef.current) {
        return getUserPromiseRef.current;
      }

      try {
        const promise = fetchUserFromAPI(tokenParam);
        getUserPromiseRef.current = promise;

        const data = await promise;
        if (data) {
          if (data.isBlocked === true) {
            if (!isLoggingOut) {
              clearAuthData();
              sessionStorage.clear();
              setAuthorization(null);
              setUserProfile(null);
              setSettings(null);
              navigate("/account/blocked", { replace: true });
            }
            return null;
          }

          setUserProfile(data);
          if (data?.settings) {
            setSettings(data.settings);
          }

          const token = tokenParam || getAuthToken();
          if (token) {
            const session = {
              aud: process.env.REACT_APP_AUTH_WEB_CLIENT,
              email: data.email,
              emailVerified: data.emailVerified,
              name: `${data.personal_info?.name || ""} ${
                data.personal_info?.lastname || ""
              }`.trim(),
              updatedAt: data.updatedAt,
              sub: data.authId || data.userId,
            };

            setAuthData({
              token: token,
              userId: data.authId || data.userId,
              email: data.email,
              session: session,
              accountId: data.accountId,
            });
          }

          return data;
        }
        return null;
      } catch (error) {
        console.error("Error in refreshUser:", error);
        return null;
      } finally {
        getUserPromiseRef.current = null;
      }
    },
    [fetchUserFromAPI, isLoggingOut, navigate]
  );

  const signInUser = useCallback(
    async (email, password) => {
      try {
        clearAuthData();
        sessionStorage.clear();

        setUserProfile(null);
        setSettings(null);

        setAuthorization(null);

        const response = await create("/api/v1/user/sign-in", {
          email,
          password,
        });

        if (
          response.data &&
          response.data.status === false &&
          response.data.message === "user is blocked"
        ) {
          const blockedError = new Error("USER_BLOCKED");
          blockedError.isBlocked = true;
          throw blockedError;
        }

        if (response.data.requiresTwoFA) {
          localStorage.setItem("pendingId", response.data.pendingId);
          return {
            requiresTwoFA: true,
          };
        }

        const { access_token } = response.data.data;

        setAuthToken(access_token);
        setAuthorization(access_token);

        const userResponse = await getQuery("/api/v1/user/get-account");

        if (userResponse.status === 200 && userResponse.data.data) {
          const userData = userResponse.data.data;

          if (userData.isBlocked === true) {
            clearAuthData();
            sessionStorage.clear();
            setAuthorization(null);
            setUserProfile(null);
            setSettings(null);
            navigate("/account/blocked", { replace: true });
            const blockedError = new Error("USER_BLOCKED");
            blockedError.isBlocked = true;
            throw blockedError;
          }

          const session = {
            aud: process.env.REACT_APP_AUTH_WEB_CLIENT,
            email: userData.email,
            emailVerified: userData.emailVerified,
            name: `${userData.personal_info?.name || ""} ${
              userData.personal_info?.lastname || ""
            }`.trim(),
            updatedAt: userData.updatedAt,
            sub: userData.authId || userData.userId,
          };

          setAuthData({
            token: access_token,
            userId: userData.authId || userData.userId,
            email: userData.email,
            session: session,
            accountId: userData.accountId,
          });

          setUserProfile(userData);

          if (userData.profile_completed && userData.settings) {
            setSettings(userData.settings);
          }

          return userData;
        }

        throw new Error("Failed to fetch user data");
      } catch (error) {
        console.error("Sign in error:", error);

        if (error.response?.data) {
          const errorData = error.response.data;
          if (
            errorData.status === false &&
            errorData.message === "user is blocked"
          ) {
            const blockedError = new Error("USER_BLOCKED");
            blockedError.isBlocked = true;
            throw blockedError;
          }
        }

        if (error.message === "USER_BLOCKED" || error.isBlocked) {
          throw error;
        }

        throw new Error(
          error?.response?.data?.message || error?.message || "Failed to log in"
        );
      }
    },
    [navigate]
  );

  const fetchUserSettings = useCallback(
    async (userData = null, tokenParam = null) => {
      try {
        let targetUserData = userData || userProfile;

        if (!targetUserData) {
          const token = tokenParam || getAuthToken();
          if (!token) return;

          if (fetchUserSettingsPromiseRef.current) {
            const data = await fetchUserSettingsPromiseRef.current;
            if (data?.settings) setSettings(data.settings);
            if (data) setUserProfile(data);
            return;
          }

          setAuthorization(token);
          const promise = getQuery("/api/v1/user/get-account")
            .then((accountResponse) => {
              if (
                accountResponse.status === 200 &&
                accountResponse.data?.data
              ) {
                return accountResponse.data.data;
              }
              return null;
            })
            .finally(() => {
              fetchUserSettingsPromiseRef.current = null;
            });

          fetchUserSettingsPromiseRef.current = promise;
          targetUserData = await promise;
          if (targetUserData) {
            setUserProfile(targetUserData);
          }
        }

        if (!targetUserData?.profile_completed) {
          return;
        }

        if (targetUserData.settings) {
          setSettings(targetUserData.settings);
        }
      } catch (err) {
        console.error("Could not get account settings:", err);
        fetchUserSettingsPromiseRef.current = null;
      }
    },
    [userProfile]
  );

  const updateSettings = useCallback((updatedSettings) => {
    setSettings((prevSettings) => ({ ...prevSettings, ...updatedSettings }));
  }, []);

  const updateProfile = useCallback((updatedProfile) => {
    setUserProfile((prevProfile) => ({ ...prevProfile, ...updatedProfile }));
  }, []);

  const handleSocialAuthCallback = useCallback(async () => {
    clearAuthData();
    sessionStorage.clear();

    setUserProfile(null);
    setSettings(null);

    setAuthorization(null);

    if (!auth0IsAuthenticated || !auth0User || socialAuthProcessedRef.current) {
      return;
    }

    try {
      socialAuthProcessedRef.current = true;

      const auth0Token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH_AUDIENCE,
          scope: "openid profile email",
        },
      });
      const access_token = auth0Token;
      setAuthToken(access_token);
      setAuthorization(access_token);

      const userData = await fetchUserFromAPI(auth0Token);

      if (userData) {
        if (userData.isBlocked === true) {
          clearAuthData();
          sessionStorage.clear();
          setAuthorization(null);
          setUserProfile(null);
          setSettings(null);
          navigate("/account/blocked", { replace: true });
          return;
        }

        const session = {
          aud: process.env.REACT_APP_AUTH_WEB_CLIENT,
          email: userData.email,
          emailVerified: userData.emailVerified,
          name: `${userData.personal_info?.name || ""} ${
            userData.personal_info?.lastname || ""
          }`.trim(),
          updatedAt: userData.updatedAt,
          sub: userData.authId || userData.userId,
        };

        setAuthData({
          token: access_token,
          userId: userData.authId || userData.userId,
          email: userData.email,
          session: session,
          accountId: userData.accountId,
        });

        setUserProfile(userData);
        await fetchUserSettings(userData);

        if (!userData.emailVerified) {
          navigate("/onboarding/verify-email");
        } else if (!userData.profile_completed) {
          const storedEmails =
            JSON.parse(localStorage.getItem("verifiedEmails")) || [];
          const emailVerifiedFromStorage = storedEmails.includes(
            userProfile?.email
          );
          if (emailVerifiedFromStorage) {
            navigate("/onboarding/complete-profile");
          } else {
            navigate("/onboarding/welcome");
          }
        } else {
          navigate("/");
        }
      }
    } catch (error) {
      console.error("Social auth callback error:", error);
      socialAuthProcessedRef.current = false;
    }
  }, [
    auth0IsAuthenticated,
    auth0User,
    getAccessTokenSilently,
    fetchUserSettings,
    fetchUserFromAPI,
    navigate,
    userProfile?.email,
  ]);

  useEffect(() => {
    const pendingId = localStorage.getItem("pendingId");
    if (pendingId) {
      setLoading(false);
      return;
    }

    const initializeAuth = async () => {
      if (initialLoadRef.current || isLoggingOut) return;
      initialLoadRef.current = true;

      try {
        setLoading(true);

        const backendToken = await getAuthToken();
        const tokenValidtity = await isTokenValid(backendToken);
        if (backendToken && tokenValidtity) {
          setAuthorization(backendToken);
          try {
            const userData = await getUser(backendToken);
            if (userData) {
              if (userData.isBlocked === true) {
                if (!isLoggingOut) {
                  clearAuthData();
                  sessionStorage.clear();
                  setAuthorization(null);
                  setUserProfile(null);
                  setSettings(null);
                  navigate("/account/blocked", { replace: true });
                }
                return;
              }

              await fetchUserSettings(userData, backendToken).catch(
                console.error
              );
              setLoading(false);
            }
          } catch (error) {
            console.error("Failed to get user with backend token:", error);
            clearAuthData();
            sessionStorage.clear();
            setAuthorization(null);
            setUserProfile(null);
            setSettings(null);
          }
        }
        if (!tokenValidtity) {
          clearAuthData();
          sessionStorage.clear();
          setAuthorization(null);
          setUserProfile(null);
          setSettings(null);
        }

        if (auth0IsAuthenticated && auth0User && !backendToken) {
          await handleSocialAuthCallback();
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
      } finally {
        setLoading(false);
      }
    };

    if (!auth0IsLoading && !isLoggingOut) {
      initializeAuth();
    }
  }, [
    auth0IsAuthenticated,
    auth0User,
    auth0IsLoading,
    getUser,
    fetchUserSettings,
    handleSocialAuthCallback,
    isLoggingOut,
    navigate,
  ]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      userProfile,
      user: userProfile,
      settings,
      loading,
      isLoggingOut,

      signInUser,
      refreshUser,
      handleLogout,
      logout: handleLogout,

      updateProfile,
      updateSettings,
      fetchUserSettings,
      getUser,
      getAccountDetails: refreshUser,

      currentUser: userProfile,
    }),
    [
      isAuthenticated,
      userProfile,
      settings,
      loading,
      isLoggingOut,
      signInUser,
      refreshUser,
      handleLogout,
      updateProfile,
      updateSettings,
      fetchUserSettings,
      getUser,
    ]
  );

  if (auth0IsLoading || loading || isLoggingOut) {
    return <SpinningLoader />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const useAuthNew = useAuth;
